import type { LucideIcon } from 'lucide-react';
import {
  Building2,
  ChartColumn,
  ClipboardList,
  Compass,
  FileText,
  FlaskConical,
  FolderCheck,
  LayoutDashboard,
  Package,
  Search,
  Vault,
} from 'lucide-react';

/**
 * Primary navigation data — single source for the Sidebar links and the
 * BreadcrumbBar's route → label lookup. The hrefs and labels are the same
 * eight sections the old tab strip carried, plus Simulators (previously
 * only reachable from dashboard cards), grouped for the sidebar.
 */
export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  id: string;
  /** One-line "what this does" shown in the sidebar hover tooltip. Copy is
   *  carried over VERBATIM from the dashboard's Platform Functions cards
   *  (which this sidebar will eventually replace). */
  description: string;
  /** Planned section with no route on this branch yet — the sidebar renders
   *  it visible but non-navigable ("Coming soon") so a click can't land on
   *  a bare 404 outside the shell. */
  soon?: boolean;
  /** Onboarding-tour target key (`data-tour`), when a tour step points here. */
  tourKey?: string;
  /** Visible only to the account OWNER (and platform admins) — spec D.1.
   *
   *  HIDDEN, not disabled: a greyed-out link still discloses that the section
   *  exists and who it is for, which is the thing the rule withholds.
   *  Filtered by every renderer of NAV_GROUPS (Sidebar + MobileNav), and the
   *  endpoint enforces it independently — the nav is convenience, the API is
   *  the boundary. */
  ownerOnly?: boolean;
}

export interface NavGroup {
  label: string;
  items: NavItem[];
  /** Onboarding-tour target key for this group's section header. */
  tourKey?: string;
}

export const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Workspace',
    items: [
      {
        to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, id: 'sideNavDashboard',
        description: 'Your regulatory workspace at a glance — KPIs, setup progress, and quick access to every platform function.',
      },
      {
        to: '/customer-account', label: 'Company Account', icon: Building2, id: 'sideNavCustomer',
        description: 'Set up new customer profiles with complete organizational and contact information for regulatory compliance management.',
        tourKey: 'nav-company-account',
      },
      {
        to: '/product-account', label: 'Product Account', icon: Package, id: 'sideNavProduct',
        description: 'Register new medical products, devices, or pharmaceuticals in the system with complete regulatory documentation.',
        tourKey: 'nav-product-account',
      },
    ],
  },
  {
    label: 'REGARA AI Agents',
    tourKey: 'nav-agents-header',
    items: [
      {
        to: '/regulatory-strategy', label: 'Regulatory Strategy', icon: Compass, id: 'sideNavStrategy',
        description: 'Develop comprehensive regulatory pathways and compliance strategies for market approval across global jurisdictions.',
      },
      {
        to: '/predicate-finder', label: 'Existing Products', icon: Search, id: 'sideNavPredicate',
        description: 'Find the existing product your submission is compared against — a predicate or comparator device, a reference drug, or a reference biologic — then choose the one that anchors your submission.',
      },
      {
        to: '/clinical-protocols', label: 'Clinical Protocols', icon: ClipboardList, id: 'sideNavClinicalProtocol',
        description: 'Design detailed clinical trial protocols with methodology, endpoints, and compliance requirements for regulatory submissions.',
      },
      {
        to: '/clinical-reports', label: 'Clinical Study Report', icon: FileText, id: 'sideNavClinicalReport',
        description: 'Draft an ICH E3 Clinical Study Report from your completed trial data — efficacy results, safety profile, demographics, and conclusions in regulator-ready structure.',
      },
      {
        to: '/submission-dossier', label: 'Submission Dossier', icon: FolderCheck, id: 'sideNavDossier',
        description: 'Assemble complete regulatory submission packages for the agencies you plan to file with, with all required documentation.',
      },
    ],
  },
  {
    label: 'Tools',
    tourKey: 'nav-tools',
    items: [
      {
        to: '/simulators', label: 'Simulators', icon: FlaskConical, id: 'sideNavSimulators',
        description: 'Run interactive simulations for regulatory strategies, clinical trials, and submission scenarios.',
      },
      {
        to: '/vault', label: 'Vault & Collaboration', icon: Vault, id: 'sideNavVault',
        description: 'Securely store and manage all regulatory documents, clinical data, and submission files with version control.',
      },
      {
        to: '/reports', label: 'Reports', icon: ChartColumn, id: 'sideNavReports', soon: true,
        description: 'Generate comprehensive compliance reports, audit trails, and post-market surveillance documentation.',
      },
    ],
  },
];

export const NAV_ITEMS: NavItem[] = NAV_GROUPS.flatMap((g) => g.items);

/**
 * The nav item whose href is a path-segment prefix of `pathname`
 * (`/submission-dossier/42` matches `/submission-dossier`;
 * `/predicate-finder-result` does NOT match `/predicate-finder`).
 * Longest match wins. Null for routes outside the nav (profile, team, …).
 */
export function navItemForPath(pathname: string): NavItem | null {
  let best: NavItem | null = null;
  for (const item of NAV_ITEMS) {
    if (pathname === item.to || pathname.startsWith(`${item.to}/`)) {
      if (!best || item.to.length > best.to.length) best = item;
    }
  }
  return best;
}


/** Nav items this user may see. Owner-only entries are dropped, not disabled. */
export function visibleNavGroups(
  user: { roles?: string[]; is_founder?: boolean } | null | undefined,
): NavGroup[] {
  const isOwner = Boolean(user?.is_founder);
  return NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => !item.ownerOnly || isOwner),
  })).filter((group) => group.items.length > 0);
}
